import { useMutation } from '@apollo/client';
import { useLingui } from '@lingui/react';
import { useEffect } from 'react';
import { styled } from 'styled-components';
import { GQL_UPDATE_TRACKING_THEMATIQUE } from '~/gql/tracking-thematique/tracking-thematique';
import useInactivityDetect from '~/helpers/hooks/activity-detect';
import useSoundAudio from '~/helpers/hooks/soundAudio';
import { useTimer } from '~/helpers/hooks/timer';
import useConnectionStore from '~/helpers/store/connection/connection';
import { changeActivityOrder } from '~/pages/components/game/activities-forms/utils';

import { SPRecapItem } from '~/pages/components/styles/styles';
import Button from '~/pages/components/ui/button';
import { QuestionItem } from '~/pages/components/ui/question-item';
import { classic } from '~/themes/classic';
import { TActivity } from '~/utils/airtable/types/types';
import { renderFullPath } from '~/utils/common/utils';

type IProps = {
  currentActivity: TActivity;
  nextActivity: () => void;
};

function Recap({ currentActivity, nextActivity }: IProps) {
  const { i18n } = useLingui();
  const { time, start, stop, clear } = useTimer();
  const { playSound } = useSoundAudio();
  const { currentTrackingThematiqueId, setCurrentTrackings, currentTrackings } =
    useConnectionStore();

  const [updateTrackingThematique] = useMutation(
    GQL_UPDATE_TRACKING_THEMATIQUE
  );

  const isActive = useInactivityDetect({ timeout: 5000 });

  useEffect(() => {
    if (!isActive) {
      stop();
    } else {
      start();
    }
  }, [isActive]);

  useEffect(() => {
    if (currentTrackingThematiqueId) {
      // setCurrentActivityOrder(currentActivity.order);
      setCurrentTrackings(
        changeActivityOrder(
          currentTrackingThematiqueId,
          currentActivity.order,
          currentTrackings
        )
      );
    }
    start();
  }, [currentActivity]);

  async function handleClickHub() {
    stop();
    if (currentTrackingThematiqueId) {
      await updateTrackingThematique({
        variables: { id: currentTrackingThematiqueId, time: time },
      });
    }
    clear();
    playSound('click');
    nextActivity();
  }

  return (
    <Container $background={renderFullPath(currentActivity.background)}>
      <SDivBlur>
        <SDivContent>
          <SDivQuestion>
            <QuestionItem
              text={currentActivity.text}
              characterImg={renderFullPath(currentActivity.characterImg)}
              $margin="0 0 5px 0"
            />
          </SDivQuestion>
          <SDivRecap>
            <SDivRecupContent>
              <SPRecapItem>{currentActivity.recap1}</SPRecapItem>
              <SPRecapItem>{currentActivity.recap2}</SPRecapItem>
              <SPRecapItem>{currentActivity.recap3}</SPRecapItem>
            </SDivRecupContent>
          </SDivRecap>
        </SDivContent>
        <SDivBtn>
          <Button color="secondary" onClick={handleClickHub}>
            {i18n._('actions.finish')}
          </Button>
        </SDivBtn>
      </SDivBlur>
    </Container>
  );
}

export default Recap;

const Container = styled.div<{ $background?: string }>`
  align-items: center;
  background-image: ${({ $background }) =>
    $background ? `url('${$background}')` : 'none'};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  position: relative;
  width: 100%;
`;
const SDivBlur = styled.div`
  align-items: center;
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  position: relative;
  width: 100%;
`;

const SDivQuestion = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex: 0 0 30%;
  width: 100%;

  @media screen and (max-width: 415px) {
    flex: 0 0 20%;
  }
  @media screen and (max-height: 600px) {
    flex: 0 0 20%;
  }
`;

const SDivContent = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 0 0 85%;
  width: 100%;
`;

const SDivBtn = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  flex: 0 0 15%;
  width: 100%;
`;

const SDivRecap = styled.div`
  align-items: center;
  flex-direction: column;
  flex-grow: 1;
  display: flex;
  justify-content: center;
  width: 100%;
`;

const SDivRecupContent = styled.div`
  background-color: ${classic.token?.colorTextSecondary};
  border: 1px solid ${classic.token?.colorWhite};
  border-radius: 16px;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 15px;
  width: 90%;
`;
